import { CoursePrice } from '@/app/components/CoursePrice'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2 } from '@/app/components/Typography'

import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { useFormatters } from '@/app/hooks/useFormatters'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'
import { PropsWithChildren } from 'react'
import { useFormContext } from 'react-hook-form'
import { MdArrowForward } from 'react-icons/md'
import { CohortPriceEnquiryButton } from './CohortPriceEnquiryButton'

type CourseCohortsProps = {
  courseInfo: Required<Pick<ICourse, 'cohorts' | 'educationProvider' | '_id'>>
  onApply?: (cohortId: string) => Promise<void>
  disabled?: boolean
  isLoading?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export function CourseCohorts({
  courseInfo,
  onApply,
  disabled,
  isLoading,
  ...props
}: CourseCohortsProps) {
  const t = useTranslations('CoursePage')
  const { formatCohortDate, isCohortActive, isCohortFree } = useCohortHelpers()
  const { formatCohortDuration } = useFormatters()
  const { cohorts = [], educationProvider } = courseInfo
  const user = useAuth()
  const {
    formState: { isSubmitting },
  } = useFormContext()

  if (!Array.isArray(cohorts) || !cohorts.length) {
    return null
  }

  return (
    <div
      {...props}
      className={`flex flex-col gap-6 bg-grey-background py-8 md:gap-12 md:px-0 md:py-24 ${props.className}`}
    >
      <Headline2 className="text-center">{t('upcoming_cohorts')}</Headline2>
      <ul className="flex flex-col gap-2 md:container md:grid md:grid-cols-12">
        {cohorts.map(
          (
            { startDate, endDate, cost: cohortCost, location, duration, _id },
            index
          ) => {
            const shouldAskForPrice =
              isCohortActive({ startDate, endDate }) && !cohortCost

            return (
              <li
                key={index}
                className="col-span-full flex items-center justify-between gap-2 rounded-xl border bg-white px-5 py-6 md:mx-auto md:w-full lg:col-span-8 lg:col-start-3"
              >
                <div className="flex grid-cols-4 items-center gap-4 md:grid md:flex-1 md:items-baseline">
                  {!!cohortCost && (
                    <CohortDetail className="hidden md:flex" title={t('cost')}>
                      <CoursePrice
                        className="!text-base"
                        course={{ cohorts, educationProvider }}
                        selectedCohortId={_id}
                      />
                    </CohortDetail>
                  )}

                  <CohortDetail
                    title={t('duration')}
                    className="hidden md:flex"
                  >
                    {formatCohortDuration({ duration })}
                  </CohortDetail>

                  <CohortDetail title={t('dates')}>
                    {formatCohortDate({
                      startDate,
                      endDate,
                      location,
                      cost: cohortCost,
                    })}
                  </CohortDetail>

                  {location && (
                    <CohortDetail
                      className="hidden md:flex"
                      title={t('location')}
                    >
                      {location}
                    </CohortDetail>
                  )}
                </div>
                {shouldAskForPrice ? (
                  <CohortPriceEnquiryButton
                    className="ml-auto"
                    cohortId={_id}
                  />
                ) : (
                  <PrimaryButton
                    id={`course-cohorts-button--${user?.isAuthenticated ? 'redirect-to-checkout' : 'redirect-to-signup'}`}
                    disabled={
                      isLoading ||
                      (disabled && user?.isAuthenticated) ||
                      (user?.isAuthenticated &&
                        !isCohortActive({ startDate, endDate }) &&
                        !isCohortFree({ cost: cohortCost }))
                    }
                    isLoading={isLoading || isSubmitting}
                    className="ml-auto shrink-0"
                    onClick={() => onApply?.(_id)}
                    type="button"
                  >
                    {user?.isAuthenticated && !isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="mx-auto text-sm">{t('continue')}</span>
                        <MdArrowForward className="ml-auto h-4 w-4" />
                      </span>
                    ) : (
                      t('sign_up_to_apply')
                    )}
                  </PrimaryButton>
                )}
              </li>
            )
          }
        )}
      </ul>
    </div>
  )
}

function CohortDetail({
  title,
  children,
  ...props
}: PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement> & { title: string }
>) {
  return (
    <div {...props} className={`flex flex-col gap-1 ${props.className ?? ''}`}>
      <p className="font-title text-sm uppercase leading-6 text-gray-light">
        {title}
      </p>
      <div className="font-sans font-semibold capitalize leading-4 text-black">
        {children}
      </div>
    </div>
  )
}
